import { useEffect, useLayoutEffect, useRef, useState, type ComponentType, type PointerEvent as ReactPointerEvent } from 'react';
import { ChevronDown, ChevronUp, Eye } from 'lucide-react';
import { PreviewThumb } from '@/components/PreviewThumb';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { NavigatorImage } from './api';
import { nameOf } from './sections';
import { seenAt } from '../seen';

/** The width of the card that follows the pointer while one is dragged. */
export const DRAG_WIDTH = 224;

interface Props {
  image: NavigatorImage;
  /** The image's preview, or null where there is none to ask for. */
  thumbnail: string | null;
  selected: boolean;
  /** In compare mode, the pane (A–D) the image is shown in. */
  pane?: string;
  /** The reader has seen it (decided 2026-09-25): an eye in the card's top right corner, right of its name, its tooltip saying when (`image.seen_utc`). */
  seen?: boolean;
  /** Collapsed ("Collapse" at the foot of the list, seen images): the band alone, no preview. */
  collapsed?: boolean;
  /**
   * How far the Type bars stuck above and below this card's section reach, so
   * that following the selection into view never lands the card under one.
   */
  scrollMargin: { top: number; bottom: number };
  onSelect: () => void;
  /** Offered to somebody who may reorder; undefined at the group's ends. */
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  /** Whether Move up / Move down are shown at all (the group has more than this image). */
  movable: boolean;
  /** The drag hook's grab, when the image may be dragged. */
  onPointerDown?: (e: ReactPointerEvent<HTMLElement>) => void;
  /** Being carried: drawn faded where it would land. */
  ghost: boolean;
}

/**
 * The selected card's bands: a light blue tint, the same blue as its group's
 * mark — selected at a glance without a dark block on a white panel.
 */
const SELECTED_BAND = 'bg-blue-100 text-blue-950 dark:bg-blue-500/25 dark:text-blue-50';

/**
 * One image of the navigator: a band, then its preview (decided 2026-09-24).
 * The band holds the image's number in a cell of its own, running its full
 * height, beside its name — the specimen id by default, where there is one —
 * and, under the name, its stain. Below it the preview fills the card, the
 * compare pane's letter in its corner. The selected card's band takes a light
 * blue tint and the card a blue ring. An image the reader has seen shows an
 * eye in its top right corner, in the green the app says "done" with. The
 * number is its place in the whole study as shown (sections.ts). A div rather
 * than a button: a button is
 * exactly what the drag hook declines to start a drag from, so the keyboard is
 * wired by hand; Move up / Move down appear in the band only once the card
 * holds a keyboard focus, never on hover.
 */
export function ImageCard({ image, thumbnail, selected, pane, seen = false, collapsed = false, scrollMargin, onSelect, onMoveUp, onMoveDown, movable, onPointerDown, ghost }: Props) {
  // Follow the selection into view. In compare mode the slide can be chosen
  // from a pane rather than from this list — clicking a quadrant, or cycling
  // A/B/C/D — and with four slides shown the one you just picked is often
  // scrolled off. `nearest` is what makes this unobtrusive: a card already on
  // screen is left exactly where it is, so clicking a visible row never
  // shuffles the list under the cursor.
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (selected) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [selected]);
  const name = nameOf(image);
  // The preview's own height (it follows the card's width), for the fold to ease to and from.
  const previewRef = useRef<HTMLDivElement>(null);
  const [previewHeight, setPreviewHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const measure = () => setPreviewHeight(el.offsetHeight || null);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const seenWhen = seen ? seenAt(image.seen_utc) : '';

  return (
    <div
      ref={ref}
      role="button"
      tabIndex={0}
      aria-pressed={selected}
      data-sid={image.image_id}
      data-ghost={ghost ? '' : undefined}
      data-drag-width={DRAG_WIDTH}
      onPointerDown={onPointerDown}
      onClick={onSelect}
      onKeyDown={(e) => {
        // The move buttons inside have keys of their own.
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      style={{ scrollMarginTop: scrollMargin.top, scrollMarginBottom: scrollMargin.bottom, opacity: ghost ? 0.3 : 1, WebkitTouchCallout: 'none' }}
      className={cn(
        'group flex w-full cursor-pointer flex-col overflow-hidden rounded-md bg-background text-left transition-[color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
        // The one line the panel keeps: a blue edge round the slide on screen.
        selected && 'ring-2 ring-blue-500',
      )}
    >
      {/* The number runs down beside the name and, under the name, the stain. (A specimen id is the image's name when it has one.) */}
      <div className={cn('flex min-w-0 items-stretch transition-colors', selected ? SELECTED_BAND : 'bg-muted/60 group-hover:bg-muted')}>
        <OrdinalCell n={image.ordinal} selected={selected} />
        <div className="flex min-w-0 flex-1 flex-col">
          <div className="flex h-7 min-w-0 items-center">
            <span className="flex min-w-0 flex-1 items-center px-2 text-sm font-medium" title={name}>
              <span className="truncate">{name}</span>
            </span>
            {seen && (
              // In the card's top right corner (user, 2026-09-26): tucked into the band's corner, not centred on the name.
              <Tooltip>
                <TooltipTrigger asChild>
                  <span role="img" aria-label={seenWhen} className="mr-1 mt-1 inline-flex shrink-0 self-start text-emerald-600 dark:text-emerald-400">
                    <Eye size={14} aria-hidden />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">{seenWhen}</TooltipContent>
              </Tooltip>
            )}
            {movable && (
              <div className="mr-1 hidden shrink-0 items-center group-has-[:focus-visible]:flex">
                <div className="flex overflow-hidden rounded bg-background text-foreground shadow-sm">
                  <MoveButton label="Move up" Icon={ChevronUp} onClick={onMoveUp} />
                  <MoveButton label="Move down" Icon={ChevronDown} onClick={onMoveDown} />
                </div>
              </div>
            )}
          </div>
          {image.stain && (
            <div className="-mt-1 flex h-6 min-w-0 items-center px-2 text-xs opacity-80" title={image.stain.meaning}>
              <span className="truncate">{image.stain.meaning}</span>
            </div>
          )}
        </div>
      </div>
      {/* The preview fills the card below the bands, edge to edge. Collapsed, it
          folds up into the band: its box's height eases from the preview's own
          height to nothing, so the cards below slide up instead of jumping
          (user, 2026-09-27), and unfolds the same way. A measured height, not a
          0fr grid row: Chrome resolves a fractional row twice, the content
          shrinking faster than its box and the card's ground showing as a band. */}
      <div
        data-preview={collapsed ? 'collapsed' : 'open'}
        aria-hidden={collapsed || undefined}
        className="overflow-hidden transition-[height] duration-200 ease-out motion-reduce:transition-none"
        style={{ height: collapsed ? 0 : previewHeight ?? undefined }}
        // Opened from collapsed, the card was brought into view at its band's
        // size; once unfolded, it is brought in again whole.
        onTransitionEnd={(e) => {
          if (e.target === e.currentTarget && e.propertyName === 'height' && selected) ref.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        }}
      >
        <div ref={previewRef} className="relative">
          <PreviewThumb url={thumbnail} fit="cover" position={image.kind === 'document' ? 'top' : 'center'} enlarge={false} className="aspect-[5/2] w-full rounded-none border-0" />
          {pane && (
            <span className="pointer-events-none absolute right-1.5 top-1.5 h-5 min-w-5 rounded-[5px] bg-primary px-1.5 text-center text-[11px] font-semibold leading-5 text-primary-foreground shadow-sm">
              {pane}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * The image's number, as the first cell of the card's band: mono digits like
 * the app's counts (community/ui/Cards.tsx CountPill), on a shade of its own
 * rather than behind a rule (no borders in the panel, decided 2026-09-24).
 * Solid blue when the card is selected.
 */
export function OrdinalCell({ n, selected = false }: { n: number; selected?: boolean }) {
  return (
    <span
      data-ordinal={n}
      aria-label={`Image ${n}`}
      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
      className={cn(
        'flex min-w-7 shrink-0 items-center justify-center px-1.5 text-xs font-semibold tabular-nums',
        selected ? 'bg-blue-600 text-white dark:bg-blue-500' : 'bg-foreground/[0.06] text-muted-foreground',
      )}
    >
      {n}
    </span>
  );
}

function MoveButton({ label, Icon, onClick }: { label: string; Icon: ComponentType<{ className?: string }>; onClick?: () => void }) {
  return (
    <button
      type="button"
      data-nodrag
      aria-label={label}
      title={label}
      disabled={!onClick}
      onClick={(e) => {
        // Not a click on the card.
        e.stopPropagation();
        onClick?.();
      }}
      className="inline-flex h-5 w-5 items-center justify-center text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-muted-foreground"
    >
      <Icon className="h-3.5 w-3.5" />
    </button>
  );
}
